import { Injectable } from '@angular/core';
import { Equipment, Maintenance, PagedResult } from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import { ApprovalAndEquipment } from 'app/main/approvals-equipments/services/api/approvals-equipments-api.service';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EquipmentsApi {
  constructor(private readonly apollo: Apollo) {}

  listEquipments({ where, orderBy, limit, offset }): Observable<PagedResult<Equipment>> {
    return this.apollo
      .query({
        query: gql`
          query getEquipments($where: EquipmentWhereInput, $orderBy: EquipmentOrderByInput, $limit: Int, $offset:Int) {
            equipments(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                number
                createdAt
                archived
                location {
                  name
                }
                locationEnvironment {
                  name
                }
                type {
                  name
                }
                warrantySummary {
                  status
                  startDate
                  endDate
                  extendedEndDate
                }
              }
            }
          }
        `,
        variables: {
          where,
          orderBy,
          limit,
          offset
        }
      })
      .pipe(map((response) => response.data['equipments'] as PagedResult<Equipment>));
  }

  getEquipmentById(id: string): Promise<Equipment | null> {
    return this.apollo
      .query({
        query: gql`
          query getEquipmentsById($id: ID!) {
            equipment(id: $id) {
              id
              imageUrl
              name
              number
              qrCode
              createdAt
              installedAt
              purchasedAt
              notes
              archived
              locationSector {
                id
                name
              }
              locationEnvironment {
                id
                name
              }
              location {
                id
                name
                address {
                  formattedAddress
                }
              }
              type {
                id
                name
                archived
                customFields {
                  id
                  name
                  position
                  required
                  type
                  options {
                    value
                    position
                  }
                }
              }
              attachments {
                id
                path
                title
                size
                createdAt
                archived
              }
              customFields {
                id
                name
                value
                position
                type
              }
              logs {
                id
                createdAt
                updatedBy {
                  id
                  name
                }
                updatedByName
                clientIpAddress
                clientApplicationName
                snapshot {
                  id
                  name
                  number
                  archived
                  qrCode
                  notes
                  purchasedAt
                  installedAt
                  imageUrl
                  location {
                    id
                    name
                  }
                  type {
                    id
                    name
                  }
                  locationSector {
                    id
                    name
                  }
                  locationEnvironment {
                    id
                    name
                  }
                  customFields {
                    name
                    value
                    type
                    position
                  }
                  attachments {
                    id
                    title
                    path
                    createdAt
                  }
                }
              }
            }
          }
        `,
        variables: {
          id
        }
      })
      .pipe(map((response) => response.data['equipment'] as Equipment))
      .toPromise();
  }

  getEquipmentWithWarrantiesById(id: string): Promise<Equipment | null> {
    return this.apollo
      .query({
        query: gql`
          query getEquipmentsById($id: ID!) {
            equipment(id: $id) {
              id
              imageUrl
              name
              number
              qrCode
              createdAt
              installedAt
              purchasedAt
              notes
              archived
              locationSector {
                id
                name
              }
              locationEnvironment {
                id
                name
              }
              location {
                id
                name
                address {
                  formattedAddress
                }
              }
              type {
                id
                name
                archived
                customFields {
                  id
                  name
                  position
                  required
                  type
                  options {
                    value
                    position
                  }
                }
              }
              attachments {
                id
                path
                title
                size
                createdAt
                archived
              }
              customFields {
                id
                name
                value
                position
                type
              }
              logs {
                id
                createdAt
                updatedBy {
                  id
                  name
                }
                updatedByName
                clientIpAddress
                clientApplicationName
                snapshot {
                  id
                  name
                  number
                  archived
                  qrCode
                  notes
                  purchasedAt
                  installedAt
                  imageUrl
                  location {
                    id
                    name
                  }
                  type {
                    id
                    name
                  }
                  locationSector {
                    id
                    name
                  }
                  locationEnvironment {
                    id
                    name
                  }
                  customFields {
                    name
                    value
                    type
                    position
                  }
                  attachments {
                    id
                    title
                    path
                    createdAt
                  }
                }
              }
              warranties {
                id
                name
                status
                coverageDurationDays
                extendedCoverageDurationDays
                extendedValue
                endDate
                startDate
                extendedEndDate
                value
                description
                archived
                createdAt
                updatedAt
                equipment {
                  id
                  name
                }
                maintenance {
                  id
                  number
                }
                template {
                  id
                  name
                }
                createdBy {
                  id
                  name
                }
                updatedBy {
                  id
                  name
                }
                companies {
                  id
                }
                maintenancesTypes {
                  id
                  name
                }
              }
              warrantySummary {
                id
                status
                startDate
                endDate
                extendedEndDate
              }
            }
          }
        `,
        variables: {
          id
        }
      })
      .pipe(map((response) => response.data['equipment'] as Equipment))
      .toPromise();
  }

  async findEquipmentMaintenances(equipmentId: string, offset: number = 0, limit: number = 10): Promise<Maintenance[]> {
    const query = gql`query searchMaintenances($where: MaintenanceWhereInput, $limit: Int, $offset: Int) {
      maintenances(
        where: $where
        orderBy: openedAt_desc
        limit: $limit
        offset: $offset
      ){
        items {
          id
          number
          location {
            name
          }
          segment {
            id
            name
          }
          company {
            id
            name
          }
          maintenanceType {
            name
          }
          status
          openedAt
          logs (orderBy: createdAt_asc, limit: 1) {
            clientApplicationName
            clientIpAddress
            relocationReason
            updatedBy {
              id
              name
            }
          }
        }
      }
    }`;

    const result = await this.apollo.query<any>({
      query,
      variables: {
        where: {
          equipmentId_in: equipmentId,
        },
        offset,
        limit,
      }
    }).toPromise();

    return result.data.maintenances.items;
  }

  createEquipment(input: any): Observable<Equipment> {
    return this.apollo
      .query({
        query: gql`
          mutation createEquipment($input: CreateEquipmentInput!) {
            createEquipment(input: $input) {
              id
              name
              number
              imageUrl
              archived
              location {
                id
              }
              locationSector {
                id
              }
              locationEnvironment {
                id
              }
            }
          }
        `,
        variables: {
          input
        }
      })
      .pipe(map((response) => response.data['createEquipment'] as Equipment));
  }

  updateEquipment(id: string, input: any): Observable<Equipment> {
    return this.apollo
      .query({
        query: gql`
          mutation updateEquipment($id: ID!, $input: UpdateEquipmentInput!) {
            updateEquipment(id: $id, input: $input) {
              id
              name
              number
              imageUrl
              archived
              location {
                id
              }
              locationSector {
                id
              }
              locationEnvironment {
                id
              }
            }
          }
        `,
        variables: {
          id,
          input
        }
      })
      .pipe(map((response) => response.data['updateEquipment'] as Equipment));
  }

  async getApprovalAndEquipment({ approvalId, equipmentQrCode }): Promise<ApprovalAndEquipment> {
    return this.apollo.query({
      query: gql`
      query approvalAndEquipment($approvalId: ID!, $equipmentQrCode: String!) {
        approval(id: $approvalId) {
          id
          createdAt
          status
          notes
          resolvedAt
          resolvedBy {
            id
            name
            email
          }
          equipment {
            id
            originalEquipmentId
            name
            qrCode
            number
            createdAt
            notes
            archived
            type {
              name
            }
            attachments {
              id
              path
              title
              createdAt
              archived
            }
            customFields {
              id
              name
              value
              type
              position
            }
            location {
              id
              name
              address {
                formattedAddress
              }
            }
          }
          company {
            id
            name
            avatarUrl
            fieldAccountId
          }
        }

        equipments(where: { qrCode_contains: $equipmentQrCode }, limit: 1, offset: 0) {
          items {
            id
            imageUrl
            name
            number
            qrCode
            createdAt
            notes
            archived
            location {
              id
              name
              address {
                formattedAddress
              }
            }
            type {
              id
              name
              segment {
                id
              }
            }
            attachments {
              id
              path
              title
              createdAt
              archived
            }
            customFields {
              id
              name
              value
              position
              type
            }
          }
        }
      }
    `,
      variables: {
        approvalId,
        equipmentQrCode
      }
    }).toPromise()
      .then(({ data }) => {
        return {
          approval: data['approval'],
          equipment: data['equipments']['items'][0]
        };
      });
  }
}
